import { isEqual } from "lodash";
import { MosaicTrafficLightsValue } from "../../traffic-lights/MosaicTrafficLightsValue";
import {
  ADD_COLUMN,
  ADD_COLUMN_SPAN,
  ADD_NEW_HEADER,
  ADD_ROW,
  ADD_ROW_GROUP,
  ADD_ROW_SPAN,
  AitHeaderGroupData,
  AitTableAction,
  AitTablePosition,
  AitTableState,
  DUPLICATE_COLUMN,
  DUPLICATE_ROW,
  DUPLICATE_ROW_GROUP,
  GridItemChangeType,
  HIDE_OPTIONS_WINDOW,
  LOAD_STATE,
  REMOVE_COLUMN,
  REMOVE_COLUMN_SPAN,
  REMOVE_ROW,
  REMOVE_ROW_GROUP,
  REMOVE_ROW_SPAN,
  SET_DECIMAL_ALIGN_PERCENT,
  SET_REPLACEMENTS,
  SET_ROW_HEADER_COLUMNS,
  SET_SPACE_AFTER,
  SET_TRAFFIC_LIGHTS,
  SHOW_OPTIONS_WINDOW,
  TABLE_CHANGE_PUSHED,
  UPDATE_COLUMN_WIDTH,
  UPDATE_COMMENTS,
  UPDATE_HEADER_CELL,
  UPDATE_TABLE_CELL,
} from "./interface";
import { newHeaderRow } from "./newRow";
import { trAddColSpan } from "./trAddColSpan";
import { trAddColumn } from "./trAddColumn";
import { trAddRow } from "./trAddRow";
import { trAddRowGroup } from "./trAddRowGroup";
import { trAddRowSpan } from "./trAddRowSpan";
import { trDuplicateColumn } from "./trDuplicateColumn";
import { trDuplicateRow } from "./trDuplicateRow";
import { trDuplicateRowGroup } from "./trDuplicateRowGroup";
import { trProcessTable } from "./trProcessTable";
import { trRemoveColSpan } from "./trRemoveColSpan";
import { trRemoveColumn } from "./trRemoveColumn";
import { trRemoveRow } from "./trRemoveRow";
import { trRemoveRowSpan } from "./trRemoveRowSpan";
import { trUpdateColumnWidth } from "./trUpdateColumnWidth";
import { trUpdateComments } from "./trUpdateComments";

/**
 * Reducer handling table editing operations.
 *
 * The reducer accepts an `AitTableState` and an `AitTableAction` and returns
 * a new state with the requested operation applied (columns, rows,
 * duplication, comments, widths, etc.). Changes update `originalTableData`
 * and set `changed`/`coreTrafficLight` flags as appropriate.
 *
 * @template T - The cell payload type
 * @param state - Current `AitTableState` to be updated
 * @param action - Action describing the table change to apply
 * @returns The updated `AitTableState` after applying the operation
 */
export const tableReducer = <T>(
  state: AitTableState<T>,
  action: AitTableAction<T>,
): AitTableState<T> => {
  let newState = { ...state };

  switch (action.operation) {
    // Done
    case ADD_COLUMN: {
      /* istanbul ignore else */
      if (
        state.originalTableData.bodyData.length > 0 &&
        state.originalTableData.bodyData[0].rows.length > 0 &&
        state.originalTableData.bodyData[0].rows[0].cells.length < action.newColumnLength
      ) {
        const addedColumn = trAddColumn(
          state.originalTableData.headerData,
          state.originalTableData.bodyData,
          action.columnIndex,
          state.defaultCellWidth,
          state.editorProps.blankT,
          state.originalTableData.columnInformation,
          action.newColumnLength,
        );

        // Determine the cell ID to use for tracking the new column
        const newColumnIndex = action.columnIndex + 1;
        let idAfterTrafficLightIsShown = "";

        // Get the aitid of the first cell in the new column position
        /* istanbul ignore else */
        if (addedColumn.headerData && addedColumn.headerData.rows.length > 0) {
          // Use header cell if available
          idAfterTrafficLightIsShown = addedColumn.headerData.rows[0].cells[newColumnIndex].aitid;
        } else if (addedColumn.bodyData.length > 0 && addedColumn.bodyData[0].rows.length > 0) {
          // Fall back to first body cell
          idAfterTrafficLightIsShown = addedColumn.bodyData[0].rows[0].cells[newColumnIndex].aitid;
        }

        // Add the new column to changedColumns
        const newChangedColumns = [
          ...(newState.originalTableData.changedColumns ??
            // istanbul ignore next
            []),
          {
            gridItemId: newColumnIndex.toString(),
            coreTrafficLight: "ChangedUnknown" as MosaicTrafficLightsValue,
            isOptional: false,
            gridItemChangeType: GridItemChangeType.added,
            idAfterTrafficLightIsShown,
          },
        ];

        newState.originalTableData = {
          ...newState.originalTableData,
          coreTrafficLight: "ChangedUnknown",
          headerData: addedColumn.headerData,
          bodyData: addedColumn.bodyData,
          changedColumns: newChangedColumns,
          columnInformation: addedColumn.columnInformation,
        };
        /* istanbul ignore else */
        if (
          action.columnIndex <
          (newState.originalTableData.rowHeaderColumns ??
            // istanbul ignore next
            0)
        ) {
          // If the column is before the row header columns, we need to increase the count
          newState.originalTableData.rowHeaderColumns =
            (newState.originalTableData.rowHeaderColumns ??
              // istanbul ignore next
              0) + 1;
        }
      }
      newState.changed = true;
      break;
    }

    // Done
    case ADD_COLUMN_SPAN: {
      newState.changed = true;
      /* istanbul ignore else */
      if (state.originalTableData.headerData) {
        const newHeaderData = trAddColSpan(
          state.originalTableData.headerData,
          action.location,
          action.newSpan,
          state.originalTableData.rowHeaderColumns ??
            // istanbul ignore next
            0,
        );
        /* istanbul ignore else */
        if (newHeaderData) {
          newState.originalTableData = {
            ...newState.originalTableData,
            headerData: newHeaderData,
            coreTrafficLight: "ChangedUnknown",
          };
        }
      }
      break;
    }

    // Done
    case ADD_NEW_HEADER: {
      newState.changed = true;
      // Create new row
      /* istanbul ignore else */
      if (newState.originalTableData.headerData === false) {
        // Get columnIds from the first body row
        const columnIds = state.originalTableData.columnInformation.map((ci) => ci.columnId);
        newState.originalTableData = {
          ...newState.originalTableData,
          coreTrafficLight: "ChangedUnknown",
          headerData: {
            aitid: crypto.randomUUID(),
            rows: [
              newHeaderRow(
                state.defaultCellWidth,
                state.editorProps.blankT,
                state.originalTableData.bodyData![0].rows[0].cells.length,
                columnIds,
              ),
            ],
            coreTrafficLight: "BlankNotEvaluated",
          },
        };
      }
      break;
    }

    // Done
    case ADD_ROW: {
      newState.changed = true;
      // Check ok to proceed
      const addedRow = trAddRow(
        state.originalTableData.headerData,
        state.originalTableData.bodyData,
        action.location,
        action.newLength,
        state.defaultCellWidth,
        state.editorProps.blankT,
      );
      newState.originalTableData = {
        ...newState.originalTableData,
        headerData: addedRow.headerData ?? state.originalTableData.headerData,
        bodyData: addedRow.bodyData ?? state.originalTableData.bodyData,
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case ADD_ROW_GROUP: {
      newState.changed = true;
      const newBodyData = trAddRowGroup(
        state.originalTableData.bodyData,
        state.groupTemplates,
        action.newLength,
        state.defaultCellWidth,
        state.editorProps.blankT,
        action.location.rowGroup,
        action.templateName,
      );
      /* istanbul ignore else */
      if (newBodyData) {
        newState.originalTableData = {
          ...newState.originalTableData,
          bodyData: newBodyData,
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    case ADD_ROW_SPAN: {
      newState.changed = true;
      /* istanbul ignore else */
      if (state.originalTableData.headerData) {
        const newHeaderData = trAddRowSpan(
          state.originalTableData.headerData,
          action.location,
          action.newSpan,
        );
        /* istanbul ignore else */
        if (newHeaderData) {
          newState.originalTableData = {
            ...newState.originalTableData,
            headerData: newHeaderData,
            coreTrafficLight: "ChangedUnknown",
          };
        }
      }
      break;
    }

    // Done
    case DUPLICATE_COLUMN: {
      newState.changed = true;
      // Check ok to proceed
      /* istanbul ignore else */
      if (
        state.originalTableData.bodyData.length > 0 &&
        state.originalTableData.bodyData[0].rows.length > 0 &&
        state.originalTableData.bodyData[0].rows[0].cells.length < action.newColumnLength
      ) {
        const duplicatedColumn = trDuplicateColumn(
          state.originalTableData.headerData,
          state.originalTableData.bodyData,
          action.columnIndex,
          state.defaultCellWidth,
          state.editorProps.blankT,
          state.originalTableData.columnInformation,
          action.newColumnLength,
        );

        // Determine the cell ID to use for tracking the new column
        const newColumnIndex = action.columnIndex + 1;
        let idAfterTrafficLightIsShown = "";

        // Get the aitid of the first cell in the new column position
        /* istanbul ignore else */
        if (duplicatedColumn.headerData && duplicatedColumn.headerData.rows.length > 0) {
          // Use header cell if available
          idAfterTrafficLightIsShown =
            duplicatedColumn.headerData.rows[0].cells[newColumnIndex].aitid;
        } else if (
          duplicatedColumn.bodyData.length > 0 &&
          duplicatedColumn.bodyData[0].rows.length > 0
        ) {
          // Fall back to first body cell
          idAfterTrafficLightIsShown =
            duplicatedColumn.bodyData[0].rows[0].cells[newColumnIndex].aitid;
        }

        // Add the new column to changedColumns
        const newChangedColumns = [
          ...(newState.originalTableData.changedColumns ??
            // istanbul ignore next
            []),
          {
            gridItemId: newColumnIndex.toString(),
            coreTrafficLight: "ChangedUnknown" as MosaicTrafficLightsValue,
            isOptional: false,
            gridItemChangeType: GridItemChangeType.added,
            idAfterTrafficLightIsShown,
          },
        ];

        newState.originalTableData = {
          ...newState.originalTableData,
          coreTrafficLight: "ChangedUnknown",
          headerData: duplicatedColumn.headerData,
          bodyData: duplicatedColumn.bodyData,
          changedColumns: newChangedColumns,
          columnInformation: duplicatedColumn.columnInformation,
        };
        if (
          action.columnIndex <
          (newState.originalTableData.rowHeaderColumns ??
            // istanbul ignore next
            0)
        ) {
          // If the column is before the row header columns, we need to increase the count
          newState.originalTableData.rowHeaderColumns =
            (newState.originalTableData.rowHeaderColumns ??
              // istanbul ignore next
              0) + 1;
        }
      }
      break;
    }

    // Done
    case DUPLICATE_ROW: {
      newState.changed = true;
      // Add new row under the original row
      const duplicateRow = trDuplicateRow(
        state.originalTableData.headerData,
        state.originalTableData.bodyData,
        action.location,
        action.newLength,
      );
      newState.originalTableData = {
        ...newState.originalTableData,
        headerData: duplicateRow.headerData,
        bodyData: duplicateRow.bodyData,
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case DUPLICATE_ROW_GROUP: {
      newState.changed = true;
      /* istanbul ignore else */
      if (newState.originalTableData.bodyData.length < action.newLength) {
        const newBodyData = trDuplicateRowGroup(
          state.originalTableData.bodyData,
          action.location.rowGroup,
          action.newLength,
        );
        newState.originalTableData = {
          ...newState.originalTableData,
          bodyData: newBodyData,
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    // Done
    case HIDE_OPTIONS_WINDOW: {
      newState.showOptionsWindow = newState.showOptionsWindow.filter(
        (l) => !isEqual(l, action.location),
      );
      break;
    }

    // Done
    case LOAD_STATE: {
      newState = {
        ...action.newState,
        showOptionsWindow: newState.showOptionsWindow,
        /* This will be processed later */
        processedTableData: {
          headerData: false,
          bodyData: [],
          coreTrafficLight: "BlankNotEvaluated",
          decimalAlignPercent: 60,
          columnInformation: [],
        },
        changed: false,
      };
      break;
    }

    // Done
    case REMOVE_COLUMN: {
      newState.changed = true;
      const removedColumn = trRemoveColumn(
        state.originalTableData.headerData,
        state.originalTableData.bodyData,
        action.cellIds,
      );
      if (
        newState.originalTableData.rowHeaderColumns &&
        action.value < newState.originalTableData.rowHeaderColumns
      ) {
        newState.originalTableData.rowHeaderColumns = Math.max(
          0,
          newState.originalTableData.rowHeaderColumns - 1,
        );
      }

      // Update columnInformation: remove the column and decrement indices for columns after it
      const removedColumnIndex = action.value;
      const updatedColumnInformation = newState.originalTableData.columnInformation
        ?.filter((ci) => ci.originalColumnIndex !== removedColumnIndex)
        .map((ci) =>
          ci.originalColumnIndex > removedColumnIndex
            ? { ...ci, originalColumnIndex: ci.originalColumnIndex - 1 }
            : ci,
        );

      newState.originalTableData = {
        ...newState.originalTableData,
        headerData: removedColumn.headerData,
        bodyData: removedColumn.bodyData,
        coreTrafficLight: "ChangedUnknown",
        columnInformation: updatedColumnInformation,
      };
      break;
    }

    // Done
    case REMOVE_COLUMN_SPAN: {
      newState.changed = true;
      /* istanbul ignore else */
      if (state.originalTableData.headerData) {
        const newHeaderData = trRemoveColSpan(
          state.originalTableData.headerData,
          action.location,
          action.newSpan,
        );
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: newHeaderData ?? state.originalTableData.headerData,
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    // Done
    case REMOVE_ROW: {
      newState.changed = true;
      const removedRow = trRemoveRow(
        state.originalTableData.headerData,
        state.originalTableData.bodyData,
        action.location,
      );
      newState.originalTableData = {
        ...newState.originalTableData,
        headerData: removedRow.headerData ?? state.originalTableData.headerData,
        bodyData: removedRow.bodyData ?? state.originalTableData.bodyData,
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case REMOVE_ROW_GROUP: {
      newState.changed = true;
      newState.originalTableData = {
        ...newState.originalTableData,
        bodyData: newState.originalTableData.bodyData.filter(
          (rowGroup) => rowGroup.aitid !== action.location.rowGroupId,
        ),
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case REMOVE_ROW_SPAN: {
      newState.changed = true;
      /* istanbul ignore else */
      if (state.originalTableData.headerData) {
        const newHeaderData = trRemoveRowSpan(
          state.originalTableData.headerData,
          action.location,
          action.newSpan,
        );
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: newHeaderData ?? state.originalTableData.headerData,
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    // Done
    case SET_DECIMAL_ALIGN_PERCENT: {
      newState.changed = true;
      newState.originalTableData = {
        ...newState.originalTableData,
        decimalAlignPercent: action.value,
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case SET_REPLACEMENTS: {
      newState.changed = true;
      /* istanbul ignore else */
      if (
        action.location.position === AitTablePosition.header &&
        newState.originalTableData.headerData
      ) {
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: {
            ...newState.originalTableData.headerData,
            coreTrafficLight: "ChangedUnknown",
            replacements: action.replacements,
          },
          coreTrafficLight: "ChangedUnknown",
        };
      } else if (
        action.location.position === AitTablePosition.bodyRowGroup &&
        newState.originalTableData.bodyData.findIndex(
          (rg) => rg.aitid === action.location.rowGroupId,
        ) !== -1
      ) {
        newState.originalTableData = {
          ...newState.originalTableData,
          bodyData: newState.originalTableData.bodyData.map((rg) =>
            rg.aitid === action.location.rowGroupId
              ? {
                  ...rg,
                  replacements: action.replacements,
                  coreTrafficLight: "ChangedUnknown",
                }
              : rg,
          ),
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    // Done
    case SET_ROW_HEADER_COLUMNS: {
      newState.changed = true;
      newState.originalTableData = {
        ...newState.originalTableData,
        rowHeaderColumns: action.value,
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case SET_SPACE_AFTER: {
      newState.changed = true;
      newState.originalTableData = {
        ...newState.originalTableData,
        bodyData: newState.originalTableData.bodyData.map((rowGroup) =>
          rowGroup.aitid === action.location.rowGroupId
            ? {
                ...rowGroup,
                spaceAfter: action.bool,
              }
            : rowGroup,
        ),
        coreTrafficLight: "ChangedUnknown",
      };
      break;
    }

    // Done
    case SET_TRAFFIC_LIGHTS:
      newState.noTrafficLights = action.bool;
      break;

    // Done
    case TABLE_CHANGE_PUSHED: {
      newState.changed = false;
      break;
    }

    // Done
    case SHOW_OPTIONS_WINDOW: {
      newState.showOptionsWindow = [
        ...newState.showOptionsWindow.filter((l) => !isEqual(l, action.location)),
        action.location,
      ];
      break;
    }

    // Done
    case UPDATE_TABLE_CELL: {
      newState.changed = true;
      // Update the replacements in the bodyData
      /* istanbul ignore else */
      if (newState.originalTableData) {
        newState.originalTableData = {
          ...newState.originalTableData,
          coreTrafficLight: "ChangedUnknown",
          bodyData: newState.originalTableData.bodyData.map((rowGroup) => ({
            ...rowGroup,
            coreTrafficLight:
              rowGroup.aitid === action.location.rowGroupId
                ? "ChangedUnknown"
                : rowGroup.coreTrafficLight,
            rows: rowGroup.rows.map((row) => ({
              ...row,
              coreTrafficLight:
                action.location.rowId === row.aitid ? "ChangedUnknown" : row.coreTrafficLight,
              cells: row.cells.map((c) =>
                c.aitid === action.location.cellId
                  ? {
                      ...c,
                      ...action.cellData,
                      coreTrafficLight: "ChangedUnknown",
                    }
                  : c,
              ),
            })),
          })),
        };
      }
      break;
    }

    // Done
    case UPDATE_COLUMN_WIDTH: {
      newState.changed = true;
      /* istanbul ignore else */
      if (state.originalTableData && newState.originalTableData) {
        const updatedWidth = trUpdateColumnWidth(
          state.originalTableData,
          action.location.originalColumn,
          action.value,
        );
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: updatedWidth.headerData,
          bodyData: updatedWidth.bodyData,
          coreTrafficLight: "ChangedUnknown",
        };
      }
      break;
    }

    // Done
    case UPDATE_COMMENTS: {
      newState.changed = true;
      newState = trUpdateComments(newState, action);
      break;
    }

    case UPDATE_HEADER_CELL: {
      newState.changed = true;
      // Update the replacements in the bodyData
      /* istanbul ignore else */
      if (newState.originalTableData.headerData) {
        const headerData = newState.originalTableData.headerData as AitHeaderGroupData<T>;
        newState.originalTableData = {
          ...newState.originalTableData,
          coreTrafficLight: "ChangedUnknown",
          headerData: {
            ...headerData,
            coreTrafficLight: "ChangedUnknown",
            rows: headerData.rows.map((row) =>
              row.aitid === action.location.rowId
                ? {
                    ...row,
                    coreTrafficLight: "ChangedUnknown",
                    cells: row.cells.map((c) =>
                      c.aitid === action.location.cellId
                        ? {
                            ...c,
                            ...action.cellData,
                            coreTrafficLight: "ChangedUnknown",
                          }
                        : c,
                    ),
                  }
                : row,
            ),
          },
        };
      }
      break;
    }
  }

  newState.processedTableData = trProcessTable(newState);
  return newState;
};
